import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputAdornment,
  CircularProgress,
  Divider,
  Alert,
  useTheme,
  Checkbox,
  ListItemText,
  Select,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import {
  CloseOutlined,
  SaveOutlined,
  PersonOutline,
  EmailOutlined,
  PhoneOutlined,
  SecurityOutlined,
  AccountBalanceWalletOutlined,
  InfoOutlined,
  DescriptionOutlined,
  SelectAllOutlined,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import { tokens } from "../../../theme";
import {
  WALLET_APPROVAL_TYPES,
  loadSuperusersOnly,
} from "./approvalConstants";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const SgwApprovalForm = ({
  open,
  onClose,
  onSave,
  initialData = null,
  isEditing = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [superusers, setSuperusers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Form State
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedApprovalIds, setSelectedApprovalIds] = useState([]);
  const [notes, setNotes] = useState("");
  const [formErrors, setFormErrors] = useState({});

  // Controlled open state for the approvals multi-select dropdown popover
  const [approvalsMenuOpen, setApprovalsMenuOpen] = useState(false);

  // Load Superusers (Role Group ID 1 only)
  useEffect(() => {
    let isMounted = true;
    async function fetchSuperusers() {
      setLoadingUsers(true);
      try {
        const users = await loadSuperusersOnly();
        if (isMounted) {
          setSuperusers(users);
        }
      } catch (err) {
        console.error("Failed to load superusers:", err);
      } finally {
        if (isMounted) setLoadingUsers(false);
      }
    }

    if (open) {
      fetchSuperusers();
    }

    return () => {
      isMounted = false;
    };
  }, [open]);

  // Pre-fill when editing or reset when adding new
  useEffect(() => {
    if (initialData) {
      setSelectedUserId(initialData.userId || "");
      setSelectedApprovalIds(
        initialData.assignedApproval ? [initialData.assignedApproval] : []
      );
      setNotes(initialData.notes || "");
    } else {
      setSelectedUserId("");
      setSelectedApprovalIds([]);
      setNotes("");
    }
    setFormErrors({});
    setApprovalsMenuOpen(false);
  }, [initialData, open]);

  // Resolved selected user object for auto-filling Name, Email, Phone
  const selectedUser = superusers.find(
    (u) => String(u.id) === String(selectedUserId)
  );

  const handleToggleSelectAllApprovals = () => {
    if (selectedApprovalIds.length === WALLET_APPROVAL_TYPES.length) {
      setSelectedApprovalIds([]);
    } else {
      setSelectedApprovalIds(WALLET_APPROVAL_TYPES.map((a) => a.id));
    }
  };

  const handleApprovalChange = (event) => {
    const value = event.target.value;
    setSelectedApprovalIds(typeof value === "string" ? value.split(",") : value);
  };

  const validate = () => {
    const errors = {};
    if (!selectedUserId) {
      errors.selectedUserId = "Please select a superuser with Role Group ID 1";
    }
    if (!selectedApprovalIds || selectedApprovalIds.length === 0) {
      errors.selectedApprovalIds =
        "Please select at least one Wallet Management approval";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Automatic date pickup (Current local date)
    const autoPickedDate = new Date().toISOString().slice(0, 10);

    // Read current logged in admin for audit maker
    let currentAdminName = "System Governance";
    try {
      const decoded = JSON.parse(localStorage.getItem("decodedToken") || "{}");
      currentAdminName =
        decoded.Name ||
        decoded.Username ||
        decoded.unique_name ||
        "Super Admin Checker";
    } catch (err) {}

    // Generate records for each selected approval upon submission (always active "Yes")
    const records = selectedApprovalIds.map((approvalId, idx) => {
      const approvalMeta = WALLET_APPROVAL_TYPES.find((a) => a.id === approvalId);
      const uniqueSuffix = Date.now().toString().slice(-4) + idx;

      return {
        id:
          isEditing && initialData?.id && selectedApprovalIds.length === 1
            ? initialData.id
            : `SGW-APP-${selectedUser?.id || selectedUserId}-${approvalId}-${uniqueSuffix}`,
        userId: Number(selectedUser?.id || selectedUserId),
        name: selectedUser?.name || "Unknown Superuser",
        username: selectedUser?.username || "admin",
        email: selectedUser?.email || "—",
        phone: selectedUser?.phone || "—",
        roleName: selectedUser?.roleName || "Super Administrator",
        roleGroupId: 1, // Rolegroup ID 1
        assignedApproval: approvalId,
        assignedApprovalLabel:
          approvalMeta?.label || "Wallet Management Approval",
        isApproved: "Yes", // Saved automatically as active upon submission
        effectiveDate: autoPickedDate, // Automatically picked date
        assignedBy: currentAdminName,
        notes: notes.trim() || "Configured via SGW Approvals Permission Manager.",
        isDeleted: false,
        updatedAt: new Date().toISOString(),
      };
    });

    onSave(records);

    Swal.fire({
      icon: "success",
      title:
        records.length > 1
          ? `${records.length} Approvals Assigned!`
          : isEditing
          ? "Permission Updated!"
          : "Approval Assigned!",
      html: `
        <div style="font-size:0.92rem; text-align:left; padding: 4px 8px;">
          <p><b>Superuser:</b> ${selectedUser?.name} (${selectedUser?.email})</p>
          <p><b>Assigned Approvals (${records.length}):</b></p>
          <ul style="padding-left: 20px; margin-top: 4px;">
            ${records
              .map((r) => `<li>${r.assignedApprovalLabel}</li>`)
              .join("")}
          </ul>
          <p><b>Status:</b> <span style="color:#16a34a; font-weight:700;">YES (AUTHORIZED)</span></p>
          <p><b>Assigned By:</b> ${currentAdminName}</p>
          <p><b>Date:</b> ${autoPickedDate}</p>
        </div>
      `,
      confirmButtonColor: "#1766a2",
    });

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          backgroundImage: "none",
        },
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${
            isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"
          }`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "rgba(23, 102, 162, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#1766a2",
            }}
          >
            <SecurityOutlined />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
              {isEditing
                ? "Edit SGW Approval Permission"
                : "Assign SGW Approval Permission"}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[300], fontSize: "0.82rem" }}>
              Configure Superuser (Role Group ID 1) authorizations for Sababisha Global Wallet operations
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseOutlined />
        </IconButton>
      </DialogTitle>

      {/* Dialog Body Form */}
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            {/* Guidance Alert */}
            <Grid item xs={12}>
              <Alert
                severity="info"
                icon={<InfoOutlined fontSize="small" />}
                sx={{
                  borderRadius: 2,
                  fontSize: "0.83rem",
                  "& .MuiAlert-message": { width: "100%" },
                }}
              >
                <strong>Role Group ID 1 Restriction:</strong> Under maker-checker financial
                governance, only Superusers with <code>Role Group ID 1</code> can be granted
                signing authority for Sababisha Global Wallet (SGW) approvals.
              </Alert>
            </Grid>

            {/* Field 1: Dropdown for Users with Rolegroup ID 1 / Superusers Only */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mb: 1, color: colors.grey[100] }}
              >
                1. Select Superuser (Role Group ID 1 Only) *
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                label="Superuser"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                error={Boolean(formErrors.selectedUserId)}
                helperText={
                  formErrors.selectedUserId ||
                  (loadingUsers
                    ? "Loading superusers..."
                    : `Filtered to ${superusers.length} active Superuser(s) in Role Group ID 1`)
                }
                disabled={loadingUsers || isEditing}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {loadingUsers ? (
                        <CircularProgress size={18} />
                      ) : (
                        <PersonOutline sx={{ color: "#1766a2" }} />
                      )}
                    </InputAdornment>
                  ),
                }}
              >
                {superusers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5, width: "100%" }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {user.name}{" "}
                          <span style={{ opacity: 0.65, fontSize: "0.78rem" }}>
                            (@{user.username})
                          </span>
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", display: "block" }}
                        >
                          {user.email} • {user.phone}
                        </Typography>
                      </Box>
                      <Chip
                        label="Role Group 1"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{
                          ml: "auto",
                          height: 20,
                          fontSize: "0.68rem",
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Read-only Auto-populated Superuser Identity Preview */}
            {selectedUser && (
              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: isDark
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(23, 102, 162, 0.04)",
                    borderColor: isDark
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(23, 102, 162, 0.2)",
                  }}
                >
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "#1766a2",
                        display: "block",
                        mb: 1.5,
                      }}
                    >
                      Auto-Populated Superuser Identity Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PersonOutline fontSize="small" sx={{ color: "#1766a2" }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                              Full Name
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {selectedUser.name}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <EmailOutlined fontSize="small" sx={{ color: "#1766a2" }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                              Email Address
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {selectedUser.email}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PhoneOutlined fontSize="small" sx={{ color: "#1766a2" }} />
                          <Box>
                            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                              Phone Number
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {selectedUser.phone}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Field 2: Multi-Select Approvals with OK Tab Button at Bottom Right */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: colors.grey[100] }}
                >
                  2. Select Approvals in Wallet Management Module (Select One or More) *
                </Typography>
                {!isEditing && (
                  <Button
                    size="small"
                    startIcon={<SelectAllOutlined />}
                    onClick={handleToggleSelectAllApprovals}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: "#1766a2",
                    }}
                  >
                    {selectedApprovalIds.length === WALLET_APPROVAL_TYPES.length
                      ? "Deselect All"
                      : "Select All Approvals"}
                  </Button>
                )}
              </Box>

              <FormControl fullWidth size="small" error={Boolean(formErrors.selectedApprovalIds)}>
                <InputLabel id="wallet-approvals-label">Wallet Approval Permissions</InputLabel>
                <Select
                  labelId="wallet-approvals-label"
                  id="wallet-approvals-select"
                  multiple
                  open={approvalsMenuOpen}
                  onOpen={() => setApprovalsMenuOpen(true)}
                  onClose={() => setApprovalsMenuOpen(false)}
                  value={selectedApprovalIds}
                  onChange={handleApprovalChange}
                  input={<OutlinedInput label="Wallet Approval Permissions" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => {
                        const matched = WALLET_APPROVAL_TYPES.find((a) => a.id === val);
                        return (
                          <Chip
                            key={val}
                            label={matched?.label || val}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              backgroundColor: matched?.bgLight || "rgba(23, 102, 162, 0.1)",
                              color: matched?.color || "#1766a2",
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: ITEM_HEIGHT * 6.5 + ITEM_PADDING_TOP,
                        width: 480,
                      },
                    },
                    autoFocus: false,
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <AccountBalanceWalletOutlined sx={{ color: "#1766a2", ml: 1 }} />
                    </InputAdornment>
                  }
                >
                  {WALLET_APPROVAL_TYPES.map((approval) => {
                    const isChecked = selectedApprovalIds.indexOf(approval.id) > -1;
                    return (
                      <MenuItem key={approval.id} value={approval.id} sx={{ py: 1 }}>
                        <Checkbox checked={isChecked} size="small" color="primary" />
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: isChecked ? 700 : 500 }}>
                              {approval.label}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {approval.description}
                            </Typography>
                          }
                        />
                        <Chip
                          label={approval.category}
                          size="small"
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            backgroundColor: approval.bgLight,
                            color: approval.color,
                          }}
                        />
                      </MenuItem>
                    );
                  })}

                  {/* OK Tab Button at Bottom Right of the Popover */}
                  <Box
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: isDark ? colors.primary[400] : "#ffffff",
                      borderTop: `1px solid ${
                        isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"
                      }`,
                      py: 1,
                      px: 2,
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      zIndex: 10,
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setApprovalsMenuOpen(false);
                      }}
                      sx={{
                        backgroundColor: "#1766a2",
                        "&:hover": { backgroundColor: "#114c79" },
                        color: "#ffffff",
                        fontWeight: 700,
                        minWidth: 72,
                        height: 30,
                        borderRadius: 1.5,
                        textTransform: "none",
                        fontSize: "0.82rem",
                        boxShadow: "none",
                      }}
                    >
                      OK
                    </Button>
                  </Box>
                </Select>
                {formErrors.selectedApprovalIds && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.selectedApprovalIds}
                  </Typography>
                )}
              </FormControl>
              <Typography variant="caption" sx={{ color: colors.grey[400], mt: 0.5, display: "block" }}>
                {selectedApprovalIds.length > 0
                  ? `${selectedApprovalIds.length} approval permission(s) selected. Submitting will populate all selected records to the table.`
                  : "You can select multiple approvals or click 'Select All Approvals'."}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
            </Grid>

            {/* Field 3: Notes / Reason */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Notes / Justification"
                placeholder="e.g. Authorized under financial delegation of authority"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionOutlined sx={{ fontSize: 18, color: "#1766a2" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${
              isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"
            }`,
            gap: 1,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
              color: colors.grey[200],
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveOutlined />}
            sx={{
              backgroundColor: "#1766a2",
              "&:hover": { backgroundColor: "#114c79" },
              color: "#ffffff",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
            }}
          >
            {isEditing
              ? "Save Changes"
              : selectedApprovalIds.length > 1
              ? `Save & Populate (${selectedApprovalIds.length}) Approvals`
              : "Submit / Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SgwApprovalForm;
